import { useCallback, useEffect, useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import api, { resolveImageUrl } from "../lib/api";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Sparkles, Check, X, Loader2, MessageSquareQuote, Inbox } from "lucide-react";

function relativeTime(iso) {
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return d.toLocaleDateString();
}

export default function AdminSuggestionsPanel() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [acting, setActing] = useState(null);
  const [rejectingId, setRejectingId] = useState(null);
  const [rejectReason, setRejectReason] = useState("");

  const fetchPending = useCallback(async () => {
    if (!isAdmin) return;
    setLoading(true);
    try {
      const { data } = await api.get("/admin/release-suggestions?status=pending&limit=50");
      setSuggestions(data.suggestions || []);
    } catch {
      toast.error("Couldn't load suggestions");
    } finally {
      setLoading(false);
    }
  }, [isAdmin]);

  useEffect(() => {
    fetchPending();
  }, [fetchPending]);

  if (!isAdmin) return null;

  const handleApprove = async (s) => {
    setActing(s.suggestion_id);
    try {
      const { data } = await api.post(`/admin/release-suggestions/${s.suggestion_id}/approve`);
      toast.success(
        data.linked_custom_entries > 0
          ? `Approved & linked ${data.linked_custom_entries} custom entries`
          : "Approved — added to catalog"
      );
      setSuggestions((list) => list.filter((x) => x.suggestion_id !== s.suggestion_id));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Approve failed");
    } finally {
      setActing(null);
    }
  };

  const handleReject = async (s) => {
    setActing(s.suggestion_id);
    try {
      await api.post(`/admin/release-suggestions/${s.suggestion_id}/reject`, {
        reason: rejectReason.trim(),
      });
      toast.success("Suggestion rejected");
      setSuggestions((list) => list.filter((x) => x.suggestion_id !== s.suggestion_id));
      setRejectingId(null);
      setRejectReason("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Reject failed");
    } finally {
      setActing(null);
    }
  };

  return (
    <div data-testid="admin-suggestions-panel" className="bg-gradient-to-br from-zinc-900 via-zinc-900 to-purple-950/20 border border-purple-500/15 rounded-xl p-5">
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-heading text-lg font-semibold text-zinc-100 flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-purple-400" /> Release Suggestions
          {suggestions.length > 0 && (
            <span className="text-[10px] font-mono text-purple-300 bg-purple-500/15 border border-purple-500/30 rounded-full px-2 py-0.5 uppercase tracking-wider">
              {suggestions.length} pending
            </span>
          )}
        </h2>
        <Button
          data-testid="refresh-suggestions"
          variant="ghost"
          size="sm"
          onClick={fetchPending}
          disabled={loading}
          className="text-zinc-500 hover:text-zinc-100"
        >
          {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Refresh"}
        </Button>
      </div>

      {loading && suggestions.length === 0 && (
        <div className="flex justify-center py-8">
          <Loader2 className="w-5 h-5 animate-spin text-purple-400" />
        </div>
      )}

      {!loading && suggestions.length === 0 && (
        <div className="text-center py-8 rounded-lg bg-zinc-900/40 border border-dashed border-zinc-800 text-zinc-500">
          <Inbox className="w-6 h-6 text-zinc-700 mx-auto mb-1.5" />
          <p className="text-xs">No pending suggestions.</p>
        </div>
      )}

      <div className="space-y-3">
        {suggestions.map((s) => (
          <div
            key={s.suggestion_id}
            data-testid={`suggestion-${s.suggestion_id}`}
            className="bg-zinc-900/60 border border-zinc-800 rounded-lg p-3"
          >
            <div className="flex items-start gap-3">
              <div className="w-14 h-14 rounded bg-zinc-950 border border-zinc-800 overflow-hidden flex-shrink-0">
                {s.model_image ? (
                  <img src={resolveImageUrl(s.model_image)} alt="" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-[8px] text-zinc-600 font-mono">
                    {s.brand?.slice(0, 3).toUpperCase() || "—"}
                  </div>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-baseline gap-1.5">
                  <span className="text-xs font-mono text-purple-400 uppercase tracking-wider">{s.brand}</span>
                  {s.variant_type && s.variant_type !== "Standard" && (
                    <span className={`text-[9px] font-mono uppercase px-1.5 rounded ${
                      s.variant_type === "Chase" ? "bg-amber-500/15 text-amber-400" : "bg-purple-500/15 text-purple-300"
                    }`}>{s.variant_type}</span>
                  )}
                </div>
                <p className="text-sm text-zinc-100 truncate">{s.release_name}</p>
                <p className="text-[11px] text-zinc-500 truncate">
                  {s.vehicle || "—"} · {s.scale || "1:64"}
                  {s.estimated_market_value ? ` · $${s.estimated_market_value}` : ""}
                  {s.upc ? ` · UPC ${s.upc}` : ""}
                </p>
                <p className="text-[10px] text-zinc-600 mt-0.5">
                  Suggested by <span className="text-zinc-400">{s.user_name}</span> · {relativeTime(s.created_at)}
                </p>
                {s.notes && (
                  <p className="text-[11px] text-zinc-400 mt-1.5 italic bg-zinc-950/60 border-l-2 border-zinc-800 pl-2 py-1">
                    <MessageSquareQuote className="w-3 h-3 inline mr-1 text-zinc-600" />
                    {s.notes}
                  </p>
                )}
              </div>
            </div>

            {rejectingId === s.suggestion_id ? (
              <div className="mt-2 flex items-center gap-2">
                <input
                  type="text"
                  placeholder="Reason (optional, sent to suggester)"
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  maxLength={500}
                  data-testid={`reject-reason-${s.suggestion_id}`}
                  className="flex-1 text-xs bg-zinc-950 border border-zinc-800 rounded px-2 py-1 text-zinc-200"
                />
                <Button
                  data-testid={`confirm-reject-${s.suggestion_id}`}
                  size="sm"
                  variant="outline"
                  onClick={() => handleReject(s)}
                  disabled={acting === s.suggestion_id}
                  className="bg-rose-500/10 border-rose-500/30 text-rose-400"
                >
                  Confirm
                </Button>
                <Button size="sm" variant="ghost" onClick={() => { setRejectingId(null); setRejectReason(""); }} className="text-zinc-500">
                  Cancel
                </Button>
              </div>
            ) : (
              <div className="mt-2 flex items-center gap-2">
                <Button
                  data-testid={`approve-suggestion-${s.suggestion_id}`}
                  size="sm"
                  onClick={() => handleApprove(s)}
                  disabled={acting === s.suggestion_id}
                  className="bg-emerald-600 hover:bg-emerald-500 text-white"
                >
                  {acting === s.suggestion_id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Check className="w-3.5 h-3.5 mr-1" /> Approve</>}
                </Button>
                <Button
                  data-testid={`reject-suggestion-${s.suggestion_id}`}
                  size="sm"
                  variant="outline"
                  onClick={() => setRejectingId(s.suggestion_id)}
                  disabled={acting === s.suggestion_id}
                  className="bg-zinc-900 border-zinc-800 text-zinc-400 hover:text-rose-400"
                >
                  <X className="w-3.5 h-3.5 mr-1" /> Reject
                </Button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
